import { useEffect, useRef, useState } from "react";
import styles from "./PhoneField.module.css";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { CuidaSearchOutline } from "./icon/CuidaSearchOutline";
import { countries } from "./countries";

function PhoneField(props) {
  const {
    error,
    onChangePhone,
    clearPhoneError,
    handleCountryChangeReset,
    phone,
    onKeyDown,
  } = props;
  const [country, setCountry] = useState(countries[0]?.label);
  const [countryCode, setCountryCode] = useState("");
  const [phoneFormatted, setPhoneFormatted] = useState("");
  const [number, setNumber] = useState("");
  const sortedCountries = [...countries].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const inputRef = useRef(null);

  const parsePhoneNumber = (phone, countries) => {
    const normalizedPhone = phone?.startsWith("+") ? phone : `+${phone}`;
    const matchedCountry = countries?.find(({ phone: countryCode }) =>
      normalizedPhone?.startsWith(countryCode)
    );
    if (!matchedCountry) {
      return { countryCodeSet: "", phoneFormatted: phone };
    }

    const { phone: countryCodeSet, label } = matchedCountry;
    setCountry(label);
    const phoneFormattedSet = normalizedPhone
      ?.replace(countryCodeSet, "")
      ?.trim();

    return { phoneFormattedSet };
  };

  useEffect(() => {
    setNumber(phone);
  }, [phone]);

  useEffect(() => {
    if (phone?.length !== 0) {
      const { phoneFormattedSet } = parsePhoneNumber(
        phone?.replace(/\D/g, ""),
        countries
      );
      const format = countries.find(
        (countryData) => countryData?.label === country
      )?.format;

      const formattedPhone = formatPhoneNumber(phoneFormattedSet, format);

      setPhoneFormatted(formattedPhone);
    }
    if (country !== "") {
      const code = countries?.find(
        (countryData) => countryData?.label === country
      )?.phone;
      setCountryCode(code);
    }
  }, [country, number]);

  useEffect(() => {
    if (countryCode && phoneFormatted) {
      onChangePhone(countryCode, phoneFormatted);
    }
  }, [countryCode, phoneFormatted]);

  const handleChangeCountry = (event) => {
    const selectedCountry = event.target.value;
    setCountry(selectedCountry);
    setPhoneFormatted("");
    clearPhoneError();
    handleCountryChangeReset();
  };

  const handleChange = (event) => {
    const input = event.target.value;
    const cursorPosition = event.target.selectionStart;
    const unformattedInput = input?.replace(/\D/g, "");
    const format = countries.find(
      (countryData) => countryData?.label === country
    )?.format;

    const formattedPhone = formatPhoneNumber(unformattedInput, format);
    setPhoneFormatted(formattedPhone);
    clearPhoneError();
    setTimeout(() => {
      const newCursorPosition = calculateCursorPosition(
        cursorPosition,
        input,
        formattedPhone
      );
      if (inputRef.current) {
        inputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);
  };

  const formatPhoneNumber = (value, format) => {
    const maxDigits = props.maxDigits;
    const digits = maxDigits ? value?.slice(0, maxDigits) : value;
    let formatted = "";
    let digitIndex = 0;

    if (!format) {
      return digits;
    }

    for (let i = 0; i < format?.length && digitIndex < digits?.length; i++) {
      if (format[i] === "#") {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        formatted += format[i];
      }
    }

    if (maxDigits && digitIndex < digits?.length) {
      formatted += digits.slice(digitIndex);
    }

    return formatted;
  };

  const calculateCursorPosition = (initialPosition, oldValue, newValue) => {
    let position = initialPosition;
    let digitCountBeforeCursor = 0;

    for (let i = 0; i < initialPosition; i++) {
      if (/\d/.test(oldValue[i])) {
        digitCountBeforeCursor++;
      }
    }

    let digitCountInNewValue = 0;
    for (let i = 0; i < newValue?.length; i++) {
      if (/\d/.test(newValue[i])) {
        digitCountInNewValue++;
      }
      if (digitCountInNewValue === digitCountBeforeCursor) {
        position = i + 1;
        break;
      }
    }

    return position;
  };

  const handleClick = () => {
    onChangePhone(countryCode, phoneFormatted);
  };

  return (
    <div className={styles.PhoneField}>
      <div className={props.PhoneHorizontal}>
        <FormControl
          fullWidth
          error={error}
          className="PhoneHorizontalSelectField"
        >
          <Select
            value={country}
            onChange={handleChangeCountry}
            className={styles.Select}
          >
            {props.Search && (
              <div className={styles.PhoneFieldSearch}>
                <CuidaSearchOutline />
                <TextField className={styles.SearchTextField} error={error} />
              </div>
            )}
            {sortedCountries?.map((item) => (
              <MenuItem
                key={item.label}
                className={styles.SelectMenuItem}
                value={item.label}
                sx={{
                  "&.Mui-selected": { color: "#5423FF !important" },
                  "&.Mui-selected svg": { display: "block !important" },
                }}
              >
                <div className={styles.Flag}>{item.flag}</div>
                <div className={styles.CountryName}>{item.label}</div>
                <div className={styles.DialCode}>{item.phone}</div>
                <CheckCircleIcon
                  className="PhoneFieldCheckIcon"
                  style={{ display: "none" }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className="PhoneHorizontalTextField">
          <TextField
            error={error}
            className={styles.PhoneTextField}
            inputRef={inputRef}
            onChange={handleChange}
            onClick={handleClick}
            onKeyDown={onKeyDown}
            value={phoneFormatted}
            autofill="tel"
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{countryCode}</InputAdornment>
              ),
            }}
          />
        </div>
      </div>
      <p className="PhoneHelperText">{props.helperText}</p>
    </div>
  );
}

export default PhoneField;
